import { useState } from 'react';

const COLORS = {
  resolve: '#0088bf',
  'resolve-mais': '#0f766e',
  'guaibim-resolve': '#d97706'
};

const WIDTH = 760;
const ROW_HEIGHT = 96;
const PAD = { top: 10, right: 28, bottom: 32, left: 132 };

/**
 * Draws the monthly quotation values of the three products.
 */
function ProductQuotationsChart({ series, loading, error }) {
  const months = series?.months || [];
  const [activeKey, setActiveKey] = useState(null);
  const selected = months.find((month) => month.key === activeKey) || months[months.length - 1] || null;

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between mb-5">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Cotações processadas</h3>
          <p className="text-sm text-gray-500">
            {series?.scope === 'own'
              ? 'Valor das cotações ativas, mês a mês, nos seus processamentos.'
              : 'Valor das cotações ativas, mês a mês.'}
          </p>
        </div>
        {series?.truncated && (
          <p className="text-xs text-gray-400">Últimos 24 meses</p>
        )}
      </div>

      {loading ? <ChartSkeleton /> : null}
      {!loading && error ? (
        <p className="text-sm text-red-600">Não foi possível carregar as cotações processadas.</p>
      ) : null}
      {!loading && !error && months.length === 0 ? (
        <p className="text-sm text-gray-500">Nenhuma cotação processada até agora.</p>
      ) : null}

      {!loading && !error && months.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {series.products.map((product) => (
              <ProductTotal key={product.id} product={product} total={series.totals[product.id]} />
            ))}
          </div>

          <QuotationPlot
            series={series}
            activeKey={selected?.key}
            onHover={setActiveKey}
          />

          {selected ? <MonthDetail month={selected} products={series.products} /> : null}

          <SeriesLegend products={series.products} />
          <SeriesTable series={series} />
        </>
      ) : null}
    </section>
  );
}

/**
 * Shows the summed value and quotation count of one product.
 */
function ProductTotal({ product, total }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS[product.id] }} />
        <span className="text-xs font-medium text-gray-500">{product.shortLabel}</span>
      </div>
      <p className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(total?.amount)}</p>
      <p className="text-xs text-gray-500">{formatCount(total?.count)} cotações</p>
    </div>
  );
}

/**
 * Draws one time series per product so a larger product does not flatten the others.
 */
function QuotationPlot({ series, activeKey, onHover }) {
  const months = series.months;
  const height = PAD.top + series.products.length * ROW_HEIGHT + PAD.bottom;
  const activeIndex = months.findIndex((month) => month.key === activeKey);

  /**
   * Highlights the month closest to the pointer.
   */
  const hoverMonth = (event) => {
    const index = nearestMonth(event, months.length);
    onHover(months[index].key);
  };

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        className="w-full h-auto"
        role="img"
        aria-label="Gráfico de valores das cotações processadas mês a mês"
        onMouseMove={hoverMonth}
        onMouseLeave={() => onHover(null)}
      >
        {activeIndex >= 0 ? (
          <line
            x1={xAt(activeIndex, months.length)}
            x2={xAt(activeIndex, months.length)}
            y1={PAD.top}
            y2={height - PAD.bottom}
            stroke="#cbd5e1"
            strokeDasharray="4 4"
          />
        ) : null}

        {series.products.map((product, row) => (
          <ProductRow
            key={product.id}
            product={product}
            months={months}
            row={row}
            activeIndex={activeIndex}
          />
        ))}

        {months.map((month, index) => (
          showTick(index, months.length) ? (
            <text
              key={month.key}
              x={xAt(index, months.length)}
              y={height - 10}
              textAnchor={tickAnchor(index, months.length)}
              fill="#6b7280"
              fontSize="11"
            >
              {month.label}
            </text>
          ) : null
        ))}
      </svg>
    </div>
  );
}

/**
 * Draws the grid, line and points of one product on its own scale.
 */
function ProductRow({ product, months, row, activeIndex }) {
  const max = niceMax(peakValue(months, product.id));
  const center = PAD.top + row * ROW_HEIGHT + ROW_HEIGHT / 2;
  const baseline = yAt(0, max, row);
  const segments = lineSegments(months, product.id, row, max);

  return (
    <g>
      <text x={12} y={center - 2} fill="#374151" fontSize="12" fontWeight="600">
        {product.shortLabel}
      </text>
      <text x={12} y={center + 14} fill="#9ca3af" fontSize="11">
        {`0 – ${formatAxis(max)}`}
      </text>
      <line x1={PAD.left} x2={WIDTH - PAD.right} y1={baseline} y2={baseline} stroke="#e5e7eb" />
      {segments.map((points) => (
        <polyline
          key={points}
          fill="none"
          stroke={COLORS[product.id]}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={points}
        />
      ))}
      {months.map((month, index) => {
        const value = month.values[product.id];
        if (value == null) return null;
        const active = index === activeIndex;
        return (
          <circle
            key={month.key}
            cx={xAt(index, months.length)}
            cy={yAt(value, max, row)}
            r={active ? 5 : 3.5}
            fill={active ? COLORS[product.id] : '#ffffff'}
            stroke={COLORS[product.id]}
            strokeWidth="2"
          />
        );
      })}
    </g>
  );
}

/**
 * Splits a product line where a month has no processed file.
 */
function lineSegments(months, productId, row, max) {
  const segments = [];
  let current = [];
  months.forEach((month, index) => {
    const value = month.values[productId];
    if (value == null) {
      if (current.length > 1) segments.push(current.join(' '));
      current = [];
      return;
    }
    current.push(`${xAt(index, months.length)},${yAt(value, max, row)}`);
  });
  if (current.length > 1) segments.push(current.join(' '));
  return segments;
}

/**
 * Lists the three product values of the month under the pointer.
 */
function MonthDetail({ month, products }) {
  return (
    <div className="mt-4 rounded-lg border border-gray-100 px-4 py-3">
      <p className="text-sm font-medium text-gray-700 mb-3">{month.label}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {products.map((product) => (
          <div key={product.id}>
            <p className="text-xs text-gray-500">
              <span className="inline-block h-2 w-2 rounded-full mr-2" style={{ backgroundColor: COLORS[product.id] }} />
              {product.shortLabel}
            </p>
            {month.values[product.id] == null ? (
              <p className="mt-1 text-sm text-gray-400">Sem processamento</p>
            ) : (
              <>
                <p className="mt-1 text-sm font-medium text-gray-900">{formatCurrency(month.values[product.id])}</p>
                <p className="text-xs text-gray-400">{formatCount(month.counts[product.id])} cotações</p>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Names the color used by each product line.
 */
function SeriesLegend({ products }) {
  return (
    <div className="mt-4 flex flex-wrap gap-4">
      {products.map((product) => (
        <span key={product.id} className="inline-flex items-center gap-2 text-xs text-gray-600">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS[product.id] }} />
          {product.label}
        </span>
      ))}
    </div>
  );
}

/**
 * Keeps the same monthly figures available to assistive technology.
 */
function SeriesTable({ series }) {
  return (
    <table className="sr-only">
      <caption>Valores mensais das cotações processadas</caption>
      <thead>
        <tr>
          <th>Mês</th>
          {series.products.map((product) => <th key={product.id}>{product.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {series.months.map((month) => (
          <tr key={month.key}>
            <td>{month.label}</td>
            {series.products.map((product) => (
              <td key={product.id}>
                {month.values[product.id] == null ? 'Sem processamento' : formatCurrency(month.values[product.id])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Reserves the chart space while the series is loading.
 */
function ChartSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="h-16 rounded-lg bg-gray-100" />
        <div className="h-16 rounded-lg bg-gray-100" />
        <div className="h-16 rounded-lg bg-gray-100" />
      </div>
      <div className="h-56 rounded-lg bg-gray-100" />
    </div>
  );
}

/**
 * Finds the largest value of one product, ignoring months without a file.
 */
function peakValue(months, productId) {
  return months.reduce((peak, month) => {
    const value = month.values[productId];
    return value == null ? peak : Math.max(peak, value);
  }, 0);
}

/**
 * Rounds the axis ceiling so the grid labels stay on even steps.
 */
function niceMax(value) {
  if (value <= 0) return 1;
  const padded = value * 1.15;
  const power = 10 ** Math.floor(Math.log10(padded));
  const fraction = padded / power;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return nice * power;
}

/**
 * Keeps the first and last month labels inside the chart.
 */
function tickAnchor(index, total) {
  if (total <= 1) return 'middle';
  if (index === 0) return 'start';
  if (index === total - 1) return 'end';
  return 'middle';
}

/**
 * Maps a month index onto the plot width.
 */
function xAt(index, count) {
  const inner = WIDTH - PAD.left - PAD.right;
  if (count <= 1) return PAD.left + inner / 2;
  return PAD.left + (index / (count - 1)) * inner;
}

/**
 * Maps a currency amount onto one product row.
 */
function yAt(value, max, row) {
  const top = PAD.top + row * ROW_HEIGHT + 18;
  const inner = ROW_HEIGHT - 28;
  return top + inner - (value / max) * inner;
}

/**
 * Keeps axis labels readable when many months are on screen.
 */
function showTick(index, total) {
  if (total <= 8) return true;
  const step = total > 16 ? 3 : 2;
  return index === 0 || index === total - 1 || index % step === 0;
}

/**
 * Converts a pointer position into the nearest month index.
 */
function nearestMonth(event, count) {
  if (count <= 1) return 0;
  const rect = event.currentTarget.getBoundingClientRect();
  const ratio = (event.clientX - rect.left) / rect.width;
  const plotLeft = PAD.left / WIDTH;
  const plotRight = (WIDTH - PAD.right) / WIDTH;
  const inner = (ratio - plotLeft) / (plotRight - plotLeft);
  return Math.min(count - 1, Math.max(0, Math.round(inner * (count - 1))));
}

/**
 * Formats a full currency amount in reais.
 */
function formatCurrency(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Formats an axis tick without crowding the plot.
 */
function formatAxis(value) {
  if (value >= 1000000) return `${trimNumber(value / 1000000)} mi`;
  if (value >= 1000) return `${trimNumber(value / 1000)} mil`;
  return trimNumber(value);
}

/**
 * Formats a number with one decimal only when it is needed.
 */
function trimNumber(value) {
  return Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

/**
 * Formats a quotation count with a thousands separator.
 */
function formatCount(value) {
  return Number(value || 0).toLocaleString('pt-BR');
}

export default ProductQuotationsChart;
